import { supabase } from '../lib/supabase';
import { twilioService } from './twilioService';

export const auctionAlertService = {
  // Get user's auction alert preferences
  async getAlertPreferences(userId) {
    try {
      const { data, error } = await supabase
        ?.from('auction_alert_preferences')?.select('*')?.eq('user_id', userId)
        ?.single();

      if (error && error?.code === 'PGRST116') {
        // No preferences found, create defaults
        return await this.createDefaultPreferences(userId);
      }

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching auction alert preferences:', error);
      return { data: null, error: error?.message || 'Failed to fetch preferences' };
    }
  },

  // Create default preferences for user
  async createDefaultPreferences(userId) {
    try {
      const { data, error } = await supabase
        ?.from('auction_alert_preferences')
        ?.insert([{ user_id: userId }])
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating default auction alert preferences:', error);
      return { data: null, error: error?.message };
    }
  },

  // Update auction alert preferences
  async updateAlertPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        ?.from('auction_alert_preferences')
        ?.upsert({
          user_id: userId,
          ...preferences,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'user_id' })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating auction alert preferences:', error);
      return { data: null, error: error?.message || 'Failed to update preferences' };
    }
  },

  // Check if alert should be sent based on frequency settings
  async shouldSendAlert(userId, alertType, auctionId) {
    try {
      const frequencyMap = {
        final_hours_alert: 'final_hours_frequency',
        outbid_alert: 'outbid_frequency',
        bid_changed_alert: 'bid_changed_frequency'
      };

      const { data: prefs } = await this.getAlertPreferences(userId);
      if (!prefs) return false;

      // Check if alert type is enabled
      if (!prefs?.[alertType]) return false;

      const frequencyKey = frequencyMap?.[alertType];
      const frequency = prefs?.[frequencyKey] || 'immediate';

      if (frequency === 'disabled') return false;
      if (frequency === 'immediate') return true;

      // Check last sent time for hourly/daily frequency
      const now = new Date();
      const cutoffHours = frequency === 'hourly' ? 1 : 24;
      const cutoffTime = new Date(now?.getTime() - cutoffHours * 60 * 60 * 1000);

      const { data: recentLogs } = await supabase
        ?.from('auction_alert_logs')
        ?.select('sent_at')
        ?.eq('user_id', userId)
        ?.eq('alert_type', alertType)
        ?.eq('auction_id', auctionId)
        ?.gte('sent_at', cutoffTime?.toISOString())
        ?.limit(1);

      return !recentLogs?.length;
    } catch (error) {
      console.error('Error checking alert frequency:', error);
      return false;
    }
  },

  // Send auction final hours alert
  async sendFinalHoursAlert(userId, auctionData) {
    try {
      const shouldSend = await this.shouldSendAlert(userId, 'final_hours_alert', auctionData?.id);
      if (!shouldSend) return { data: null, error: 'Alert throttled by frequency settings' };

      const { data: prefs } = await this.getAlertPreferences(userId);
      const hoursLeft = auctionData?.hoursRemaining || 2;

      const message = `⏰ AUCTION ALERT: "${auctionData?.title}" ends in ${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}! Current bid: $${auctionData?.currentBid?.toLocaleString()}. Don't miss out! - Crazy Rare Club`;

      return await this.sendAlert(userId, {
        auctionId: auctionData?.id,
        alertType: 'final_hours_alert',
        message,
        prefs,
        metadata: { hoursLeft, currentBid: auctionData?.currentBid, title: auctionData?.title }
      });
    } catch (error) {
      console.error('Error sending final hours alert:', error);
      return { data: null, error: error?.message };
    }
  },

  // Send outbid alert
  async sendOutbidAlert(userId, auctionData) {
    try {
      const shouldSend = await this.shouldSendAlert(userId, 'outbid_alert', auctionData?.id);
      if (!shouldSend) return { data: null, error: 'Alert throttled by frequency settings' };

      const { data: prefs } = await this.getAlertPreferences(userId);

      const message = `🔔 OUTBID: You've been outbid on "${auctionData?.title}"! New highest bid: $${auctionData?.currentBid?.toLocaleString()}. Bid now to stay in the lead! - Crazy Rare Club`;

      return await this.sendAlert(userId, {
        auctionId: auctionData?.id,
        alertType: 'outbid_alert',
        message,
        prefs,
        metadata: { currentBid: auctionData?.currentBid, title: auctionData?.title, yourBid: auctionData?.yourBid }
      });
    } catch (error) {
      console.error('Error sending outbid alert:', error);
      return { data: null, error: error?.message };
    }
  },

  // Send highest bid changed alert
  async sendBidChangedAlert(userId, auctionData) {
    try {
      const shouldSend = await this.shouldSendAlert(userId, 'bid_changed_alert', auctionData?.id);
      if (!shouldSend) return { data: null, error: 'Alert throttled by frequency settings' };

      const { data: prefs } = await this.getAlertPreferences(userId);

      const message = `📈 BID UPDATE: New highest bid on "${auctionData?.title}": $${auctionData?.currentBid?.toLocaleString()} (was $${auctionData?.previousBid?.toLocaleString()}). View auction at Crazy Rare Club.`;

      return await this.sendAlert(userId, {
        auctionId: auctionData?.id,
        alertType: 'bid_changed_alert',
        message,
        prefs,
        metadata: { currentBid: auctionData?.currentBid, previousBid: auctionData?.previousBid, title: auctionData?.title }
      });
    } catch (error) {
      console.error('Error sending bid changed alert:', error);
      return { data: null, error: error?.message };
    }
  },

  // Send auction won alert
  async sendAuctionWonAlert(userId, auctionData) {
    try {
      const { data: prefs } = await this.getAlertPreferences(userId);
      if (!prefs?.auction_won_alert) return { data: null, error: 'Alert disabled' };

      const message = `🏆 CONGRATULATIONS! You won the auction for "${auctionData?.title}" with a bid of $${auctionData?.winningBid?.toLocaleString()}! Complete your purchase at Crazy Rare Club.`;

      return await this.sendAlert(userId, {
        auctionId: auctionData?.id,
        alertType: 'auction_won_alert',
        message,
        prefs,
        metadata: { winningBid: auctionData?.winningBid, title: auctionData?.title }
      });
    } catch (error) {
      console.error('Error sending auction won alert:', error);
      return { data: null, error: error?.message };
    }
  },

  // Core alert sending logic
  async sendAlert(userId, { auctionId, alertType, message, prefs, metadata }) {
    const results = { sms: null, push: null };

    // Send SMS if enabled
    if (prefs?.sms_enabled) {
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('phone_number, phone_verified, sms_notifications_enabled')
        ?.eq('id', userId)
        ?.single();

      if (userProfile?.phone_verified && userProfile?.sms_notifications_enabled && userProfile?.phone_number) {
        const { data: smsData, error: smsError } = await twilioService?.sendSMS(
          userProfile?.phone_number,
          message
        );
        results.sms = smsError ? { error: smsError } : { success: true, sid: smsData?.messageSid };

        // Log SMS alert
        await supabase?.from('auction_alert_logs')?.insert([{
          user_id: userId,
          auction_id: auctionId,
          alert_type: alertType,
          channel: 'sms',
          message,
          status: smsError ? 'failed' : 'sent',
          metadata: { ...metadata, error: smsError }
        }]);
      }
    }

    // Log push notification (browser push - stored for display in UI)
    if (prefs?.push_enabled) {
      await supabase?.from('auction_alert_logs')?.insert([{
        user_id: userId,
        auction_id: auctionId,
        alert_type: alertType,
        channel: 'push',
        message,
        status: 'sent',
        metadata
      }]);
      results.push = { success: true };
    }

    return { data: results, error: null };
  },

  // Get recent alert logs for user
  async getAlertLogs(userId, limit = 20) {
    try {
      const { data, error } = await supabase
        ?.from('auction_alert_logs')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('sent_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching alert logs:', error);
      return { data: [], error: error?.message };
    }
  }
};

export default auctionAlertService;
